export type TimeFilterValue =
  | "6hours"
  | "12hours"
  | "24hours"
  | "48hours"
  | "1week"
  | "2weeks"
  | "30days"
  | "all";

export interface TimeFilterOption {
  label: string;
  value: TimeFilterValue;
}

export const TIME_FILTER_OPTIONS: TimeFilterOption[] = [
  { label: "6 hours", value: "6hours" },
  { label: "12 hours", value: "12hours" },
  { label: "24 hours", value: "24hours" },
  { label: "48 hours", value: "48hours" },
  { label: "1 week", value: "1week" },
  { label: "2 weeks", value: "2weeks" },
  { label: "30 days", value: "30days" },
  { label: "All time", value: "all" },
];

export const DEFAULT_TIME_FILTER: TimeFilterValue = "6hours";

export const getDateFromTimeFilter = (
  filterValue: TimeFilterValue
): Date | undefined => {
  const now = new Date();

  switch (filterValue) {
    case "6hours":
      return new Date(now.getTime() - 6 * 60 * 60 * 1000);
    case "12hours":
      return new Date(now.getTime() - 12 * 60 * 60 * 1000);
    case "24hours":
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    case "48hours":
      return new Date(now.getTime() - 48 * 60 * 60 * 1000);
    case "1week":
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case "2weeks":
      return new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    case "30days":
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    case "all":
    default:
      return undefined;
  }
};

export const getTimeFilterFromDate = (
  date: Date | string | undefined
): TimeFilterValue => {
  if (!date) return "all";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();

  // Convert to hours
  const diffHours = diffMs / (1000 * 60 * 60);

  // Find the closest match by checking which time range the date falls into
  // Use ranges that correspond to the actual filter values
  // Add small tolerance (0.1 hours = 6 minutes) to account for timing differences
  if (diffHours <= 6.1) return "6hours"; // 0-6.1 hours -> 6 hours
  if (diffHours <= 12.1) return "12hours"; // 6.1-12.1 hours -> 12 hours
  if (diffHours <= 24.1) return "24hours"; // 12.1-24.1 hours -> 24 hours
  if (diffHours <= 48.1) return "48hours"; // 24.1-48.1 hours -> 48 hours

  // Convert to days
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays <= 7.1) return "1week"; // 2-7.1 days -> 1 week
  if (diffDays <= 14.1) return "2weeks"; // 7.1-14.1 days -> 2 weeks
  if (diffDays <= 30.1) return "30days"; // 14.1-30.1 days -> 30 days

  return "all";
};
